import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MAX_TIMEOUT_MS,
  clockOffset,
  liveInterviewState,
  nextExpiryDelay,
} from "../utils/interviewExpiry";

/**
 * Re-renders the caller when any scheduled interview in `items` reaches its `expiresAt`, with
 * one timer for the whole list and no requests. Returns `stateOf(item)`, which gives the
 * state to show at render time ("INTERVIEW_EXPIRED" once the deadline has passed).
 */
export default function useInterviewExpiry(items, serverTime) {
  const offset = useMemo(
    () => (serverTime ? clockOffset(serverTime) : 0),
    [serverTime]
  );
  const [, setTick] = useState(0);

  useEffect(() => {
    let timer;
    const arm = () => {
      const delay = nextExpiryDelay(items, offset);
      if (delay === null) return;
      timer = setTimeout(() => {
        setTick((tick) => tick + 1);
        arm();
      }, Math.min(delay, MAX_TIMEOUT_MS));
    };
    arm();

    // Timers can be held back while a laptop sleeps or a tab is in the background.
    const onVisible = () => {
      if (document.visibilityState === "visible") setTick((tick) => tick + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [items, offset]);

  return useCallback(
    (item) => liveInterviewState(item?.interviewState, item?.expiresAt, offset),
    [offset]
  );
}
